import type { CondimentDTO, CondimentSaleLine } from '@six-pockets/shared';

export type QtyMap = Record<string, number>;

/** A line as the qty helpers need it (sale lines and bill lines both fit). */
type QtyLine = { condimentId: string; qty: number };
type PricedLine = QtyLine & { unitPrice: number };

export function toQtyMap(items: QtyLine[]): QtyMap {
  return Object.fromEntries(items.map((i) => [i.condimentId, i.qty]));
}

export function toItemsPayload(qty: QtyMap): { condimentId: string; qty: number }[] {
  return Object.entries(qty)
    .filter(([, n]) => n > 0)
    .map(([condimentId, n]) => ({ condimentId, qty: n }));
}

export function totalQty(qty: QtyMap): number {
  return Object.values(qty).reduce((a, n) => a + n, 0);
}

export function sameQty(a: QtyMap, b: QtyMap): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const k of keys) if ((a[k] ?? 0) !== (b[k] ?? 0)) return false;
  return true;
}

/** Preview lines for a qty map. Lines already on the tab keep the price they were added at. */
export function toItemLines(qty: QtyMap, catalog: CondimentDTO[], existing: PricedLine[]): CondimentSaleLine[] {
  return catalog
    .filter((c) => (qty[c.id] ?? 0) > 0)
    .map((c) => {
      const unitPrice = existing.find((e) => e.condimentId === c.id)?.unitPrice ?? c.sellingPrice;
      const n = qty[c.id]!;
      return { condimentId: c.id, name: c.name, unitPrice, qty: n, amount: unitPrice * n };
    });
}

/** "2× Tea, 1× Chips" */
export function itemsSummary(items: { name: string; qty: number }[]): string {
  return items.map((i) => `${i.qty}× ${i.name}`).join(', ');
}
